// Partner configuration rows in the Platform API's Azure table (Kindred.Partners.Services/Models/PartnerConfigurationEntity.cs).

/** PartitionKey for every partner, RowKey for every country. A partner's own row wins over ALL, a country row over ALL. */
export const PARTNER_CONFIGURATION_ALL = "ALL";

export const COUPON_ENFORCEMENT_MODES = ["PriorityListOnly", "None", "BestOnly", "All"] as const;
export type CouponEnforcementMode = (typeof COUPON_ENFORCEMENT_MODES)[number];

export const OFFER_DISPLAY_MODES = ["Always", "WhenNoCoupons", "WhenNoAutoCoupons", "Never"] as const;
export type OfferDisplayMode = (typeof OFFER_DISPLAY_MODES)[number];

export const COUPON_ENFORCEMENT_MODE_HINTS: Record<CouponEnforcementMode, string> = {
  PriorityListOnly: "Prioritised coupons first, sitewide templates as fallback, everything else still listed.",
  None: "Everything ranked by priority and listed; nothing is auto-applied.",
  BestOnly: "Only the single top-priority coupon is auto-applied; nothing else is surfaced.",
  All: "Every eligible coupon is an auto-coupon; the client tests them all.",
};

export const OFFER_DISPLAY_MODE_HINTS: Record<OfferDisplayMode, string> = {
  Always: "Always show offers alongside coupons.",
  WhenNoCoupons: "Show offers only when there are no coupons.",
  WhenNoAutoCoupons: "Show offers only when there are no auto-applicable coupons.",
  Never: "Never show offers.",
};

export interface IPartnerConfigurationValues {
  allowCodelessDeals: boolean;
  /** Share of requests that may get a codeless deal. Null = unthrottled. */
  codelessDealsAllowedPercentage: number | null;
  /** Kept as a string: the Platform API falls back to its default for values it can't parse. */
  couponEnforcementMode: string;
  offerDisplayMode: string;
  maxCoupons: number | null;
  maxAutoCoupons: number | null;
  maxOffers: number | null;
  currentStanddownRulesVersion: number;
  currentWhitelistDomainsVersion: number;
}

/** What a row means when a value is missing; the entity's int versions default to 0. */
export const PARTNER_CONFIGURATION_DEFAULTS: IPartnerConfigurationValues = {
  allowCodelessDeals: true,
  codelessDealsAllowedPercentage: null,
  couponEnforcementMode: "PriorityListOnly",
  offerDisplayMode: "Always",
  maxCoupons: null,
  maxAutoCoupons: null,
  maxOffers: null,
  currentStanddownRulesVersion: 0,
  currentWhitelistDomainsVersion: 0,
};

export interface IPartnerConfigurationRow extends IPartnerConfigurationValues {
  /** Lowercase partner GUID, or ALL. */
  partitionKey: string;
  /** Kindred country code (UK, not GB), or ALL. */
  rowKey: string;
  etag: string;
  timestamp: string | null;
}

export interface IPartnerConfigurationPartner {
  /** Upper-cased, as the partners DB service returns it. */
  partnerId: string;
  name: string;
}

export interface IPartnerConfigurationResponse {
  /** False when the backend has no connection string for the table. */
  configured: boolean;
  table: string;
  rows: IPartnerConfigurationRow[];
  partners: IPartnerConfigurationPartner[];
  loadedAt: string;
}

export type PartnerConfigurationChange =
  | { op: "create"; partitionKey: string; rowKey: string; values: IPartnerConfigurationValues }
  | { op: "update"; partitionKey: string; rowKey: string; etag: string; values: IPartnerConfigurationValues }
  | { op: "delete"; partitionKey: string; rowKey: string; etag: string };

export interface IPartnerConfigurationApplyRequest {
  changes: PartnerConfigurationChange[];
}

export interface IPartnerConfigurationChangeResult {
  op: PartnerConfigurationChange["op"];
  partitionKey: string;
  rowKey: string;
  ok: boolean;
  /** Someone else changed or created the row since it was loaded. */
  conflict?: boolean;
  error?: string;
}

export interface IPartnerConfigurationApplyResponse {
  /** Groups this save's history entries and its before/after snapshots. */
  batchId: string;
  applied: number;
  failed: number;
  results: IPartnerConfigurationChangeResult[];
}

export interface IPartnerConfigurationHistoryEntry {
  _id: string;
  batchId: string | null;
  op: PartnerConfigurationChange["op"];
  partitionKey: string;
  rowKey: string;
  before: IPartnerConfigurationValues | null;
  after: IPartnerConfigurationValues | null;
  changedBy: string | null;
  changedAt: string;
}

/** A row as kept in a snapshot: keys and values, no etag. */
export interface IPartnerConfigurationSnapshotRow extends IPartnerConfigurationValues {
  partitionKey: string;
  rowKey: string;
}

/**
 * initial: first pull. observed: the table changed since the last snapshot without a save here (e.g. Storage Explorer).
 * before-save / after-save: taken around every save. manual: someone asked for one.
 */
export type PartnerConfigurationSnapshotReason = "initial" | "observed" | "before-save" | "after-save" | "manual";

export interface IPartnerConfigurationRowDiff {
  op: PartnerConfigurationChange["op"];
  partitionKey: string;
  rowKey: string;
  before: IPartnerConfigurationValues | null;
  after: IPartnerConfigurationValues | null;
}

export interface IPartnerConfigurationSnapshotSummary {
  _id: string;
  reason: PartnerConfigurationSnapshotReason;
  table: string;
  batchId: string | null;
  takenBy: string | null;
  takenAt: string;
  rowCount: number;
  /** Against the snapshot before it. */
  added: number;
  changed: number;
  removed: number;
}

export interface IPartnerConfigurationSnapshot extends IPartnerConfigurationSnapshotSummary {
  rows: IPartnerConfigurationSnapshotRow[];
  diff: IPartnerConfigurationRowDiff[];
}

/** ISO 3166-1 alpha-2 in Kindred's spelling: the Platform API rewrites GB to UK. */
export const PARTNER_CONFIGURATION_COUNTRIES: string[] = (
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ " +
  "CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR " +
  "GA GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP " +
  "KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS " +
  "MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS " +
  "RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW " +
  "TZ UA UG UK UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW"
).split(" ");
